import Oystehr from '@oystehr/sdk';
import { apiErrorToThrow, chooseJson } from 'utils/lib/helpers/oystehrApi';
import { CREATE_TIMELY_FILING_REPORT_ZAMBDA, RefreshReportKind } from 'utils/lib/types/data/billing/billing.constants';
import {
  AddClaimAttachmentInputSchema,
  AddClaimNoteInputSchema,
  AddClaimWriteOffInputSchema,
  AddEraAttachmentInputSchema,
  BulkAddChargeItemDefinitionProcedureCodesInputSchema,
  CreateBillingClaimInputSchema,
  CreateBillingCoverageInputSchema,
  CreateBillingPatientInputSchema,
  CreateBillingProviderInputSchema,
  CreateChargeItemDefinitionInputSchema,
  CreateTimelyFilingReportInputSchema,
  DeleteBillingCoverageInputSchema,
  DeleteBillingProviderInputSchema,
  DeleteBillingTagInputSchema,
  DeleteChargeItemDefinitionInputSchema,
  DeleteClaimAttachmentInputSchema,
  DeleteEraAttachmentInputSchema,
  DeleteServiceFacilityInputSchema,
  DownloadClaimAttachmentInputSchema,
  DownloadEraAttachmentInputSchema,
  ExportBillingClaimsInputSchema,
  ExportClaimX12InputSchema,
  GetBillingClaimsExportStatusInputSchema,
  GetBillingCoverageInputSchema,
  GetBillingPatientBalanceInputSchema,
  GetBillingPaymentsReportDrilldownInputSchema,
  GetBillingProviderInputSchema,
  GetChargeItemDefinitionInputSchema,
  GetClaimCms1500InputSchema,
  GetClaimDetailInputSchema,
  GetClaimHistoryInputSchema,
  GetEraDetailInputSchema,
  GetPatientCoveragesInputSchema,
  GetPatientDetailInputSchema,
  GetServiceFacilityInputSchema,
  ImportEraInputSchema,
  MatchClaimResponseToClaimInputSchema,
  NetCollectionsDrilldownParamsSchema,
  PatientPaymentsDrilldownParamsSchema,
  RecordBillingManualPaymentInputSchema,
  RenameClaimAttachmentInputSchema,
  RenameEraAttachmentInputSchema,
  ReportDateWindowParams,
  SaveBillingTagInputSchema,
  SaveManualEraInputSchema,
  SaveServiceFacilityInputSchema,
  SearchBillingClaimsInputSchema,
  SearchBillingClaimTasksInputSchema,
  SearchBillingLocationsInputSchema,
  SearchBillingPatientARClaimsInputSchema,
  SearchBillingPatientsInputSchema,
  SearchBillingPayersInputSchema,
  SearchBillingProvidersInputSchema,
  SearchBillingServicesInputSchema,
  SearchChargeItemDefinitionsInputSchema,
  SearchErasInputSchema,
  SearchServiceFacilitiesInputSchema,
  TagBillingClaimInputSchema,
  UnmatchClaimResponseInputSchema,
  UpdateBillingClaimTaskInputSchema,
  UpdateBillingCoverageInputSchema,
  UpdateBillingPatientInputSchema,
  UpdateBillingProviderInputSchema,
  UpdateBillingResourceInputSchema,
  UpdateChargeItemDefinitionInputSchema,
  VoidClaimWriteOffInputSchema,
} from 'utils/lib/types/data/billing/billing.schemas';
import {
  AddClaimAttachmentResponse,
  AddEraAttachmentResponse,
  BillingChargeItemDefinition,
  BillingClaimsExportKickOffResponse,
  BillingClaimsExportStatusResponse,
  BillingCodeOption,
  BillingProviderOption,
  ClaimDetailResponse,
  CreatedClaimResponse,
  CreatedResourceResponse,
  CreateTimelyFilingReportResponse,
  DeletedResponse,
  DownloadClaimAttachmentResponse,
  DownloadEraAttachmentResponse,
  EraDetailResponse,
  ExportClaimX12Response,
  GetBillingCardsOnFileReportResponse,
  GetBillingCoverageResponse,
  GetBillingInvoiceReportResponse,
  GetBillingNetCollectionsDrilldownResponse,
  GetBillingNetCollectionsReportResponse,
  GetBillingPatientBalanceResponse,
  GetBillingPatientPaymentsDrilldownResponse,
  GetBillingPatientPaymentsReportResponse,
  GetBillingPaymentsReportDrilldownResponse,
  GetBillingPaymentsReportResponse,
  GetBillingPipelineReportResponse,
  GetBillingProductivityReportResponse,
  GetBillingReportHistoryResponse,
  GetPatientCoveragesResponse,
  OkResponse,
  PatientDetailResponse,
  RecordBillingManualPaymentResponse,
  SavedResourceResponse,
  SaveManualEraResponse,
  SearchBillingClaimsResponse,
  SearchBillingClaimTasksResponse,
  SearchBillingErasResponse,
  SearchBillingLocationsResponse,
  SearchBillingPatientARClaimsResponse,
  SearchBillingPatientsResponse,
  SearchBillingPayersResponse,
  SearchBillingProvidersResponse,
  SearchBillingServicesResponse,
  SearchBillingTagsResponse,
  SearchChargeItemDefinitionsResponse,
  SearchCodeResponse,
  SearchServiceFacilitiesResponse,
  ServiceFacilityItem,
} from 'utils/lib/types/data/billing/billing.types';
import { GetClaimHistoryResponse } from 'utils/lib/types/data/billing/claim-history';
import { Cms1500FormData } from 'utils/lib/types/data/billing/cms1500.types';
import {
  CreateCustomInsuranceOrgInputSchema,
  DeleteCustomInsuranceOrgInputSchema,
  SearchCustomInsuranceOrgsInputSchema,
  UpdateCustomInsuranceOrgInputSchema,
} from 'utils/lib/types/data/billing/custom-insurance-org.schemas';
import { SearchCustomInsuranceOrgsResponse } from 'utils/lib/types/data/billing/custom-insurance-org.types';
import {
  CreateNonInsuranceOrgInputSchema,
  DeleteNonInsuranceOrgInputSchema,
  SearchNonInsuranceOrgsInputSchema,
  UpdateNonInsuranceOrgInputSchema,
} from 'utils/lib/types/data/billing/non-insurance-org.schemas';
import { SearchNonInsuranceOrgsResponse } from 'utils/lib/types/data/billing/non-insurance-org.types';
import {
  BillingRulesResponse,
  GetBillingRulesInputSchema,
  RunBillingRulesEngineInputSchema,
  RunBillingRulesEngineResponse,
  SaveBillingRulesInputSchema,
} from 'utils/lib/types/data/billing/rules-engine.schemas';
import z from 'zod';

async function executeBillingZambda<T>(oystehr: Oystehr, id: string, parameters?: Record<string, unknown>): Promise<T> {
  try {
    const response = await oystehr.zambda.execute({
      id,
      ...parameters,
    });
    return chooseJson<T>(response);
  } catch (error: unknown) {
    throw apiErrorToThrow(error);
  }
}

// --- Rules engines ---

export const getBillingRules = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetBillingRulesInputSchema>
): Promise<BillingRulesResponse> => executeBillingZambda(oystehr, 'get-billing-rules', parameters);

export const saveBillingRules = (
  oystehr: Oystehr,
  parameters: z.input<typeof SaveBillingRulesInputSchema>
): Promise<BillingRulesResponse> => executeBillingZambda(oystehr, 'save-billing-rules', parameters);

export const runBillingRulesEngine = (
  oystehr: Oystehr,
  parameters: z.input<typeof RunBillingRulesEngineInputSchema>
): Promise<RunBillingRulesEngineResponse> => executeBillingZambda(oystehr, 'run-billing-rules-engine', parameters);

// --- Patients ---

export const createBillingPatient = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateBillingPatientInputSchema>
): Promise<CreatedResourceResponse> => executeBillingZambda(oystehr, 'create-billing-patient', parameters);

export const searchBillingPatients = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingPatientsInputSchema>
): Promise<SearchBillingPatientsResponse> => executeBillingZambda(oystehr, 'search-billing-patients', parameters);

export const getBillingPatientDetail = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetPatientDetailInputSchema>
): Promise<PatientDetailResponse> => executeBillingZambda(oystehr, 'get-billing-patient-detail', parameters);

export const updateBillingPatient = (
  oystehr: Oystehr,
  parameters: z.input<typeof UpdateBillingPatientInputSchema>
): Promise<CreatedResourceResponse> => executeBillingZambda(oystehr, 'update-billing-patient', parameters);

// --- Claims ---

export const searchBillingClaimTasks = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingClaimTasksInputSchema>
): Promise<SearchBillingClaimTasksResponse> => executeBillingZambda(oystehr, 'search-billing-claim-tasks', parameters);

export const retryBillingClaimTask = (
  oystehr: Oystehr,
  parameters: Pick<z.input<typeof UpdateBillingClaimTaskInputSchema>, 'taskId'>
): Promise<{ taskId: string }> =>
  executeBillingZambda(oystehr, 'update-billing-claim-task', { ...parameters, action: 'retry' });

export const cancelBillingClaimTask = (
  oystehr: Oystehr,
  parameters: Pick<z.input<typeof UpdateBillingClaimTaskInputSchema>, 'taskId'>
): Promise<{ taskId: string }> =>
  executeBillingZambda(oystehr, 'update-billing-claim-task', { ...parameters, action: 'cancel' });

export const createBillingClaim = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateBillingClaimInputSchema>
): Promise<CreatedClaimResponse> => executeBillingZambda(oystehr, 'create-billing-claim', parameters);

export const searchBillingClaims = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingClaimsInputSchema>
): Promise<SearchBillingClaimsResponse> => executeBillingZambda(oystehr, 'search-billing-claims', parameters);

export const exportBillingClaims = (
  oystehr: Oystehr,
  parameters: z.input<typeof ExportBillingClaimsInputSchema>
): Promise<BillingClaimsExportKickOffResponse> => executeBillingZambda(oystehr, 'export-billing-claims', parameters);

export const getBillingClaimsExportStatus = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetBillingClaimsExportStatusInputSchema>
): Promise<BillingClaimsExportStatusResponse> => executeBillingZambda(oystehr, 'export-billing-claims', parameters);

export const searchBillingPatientARClaims = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingPatientARClaimsInputSchema>
): Promise<SearchBillingPatientARClaimsResponse> =>
  executeBillingZambda(oystehr, 'search-billing-patient-ar-claims', parameters);

export const getBillingPatientBalance = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetBillingPatientBalanceInputSchema>
): Promise<GetBillingPatientBalanceResponse> =>
  executeBillingZambda(oystehr, 'get-billing-patient-balance', parameters);

export const getBillingClaimDetail = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetClaimDetailInputSchema>
): Promise<ClaimDetailResponse> => executeBillingZambda(oystehr, 'get-billing-claim-detail', parameters);

export const getBillingClaimHistory = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetClaimHistoryInputSchema>
): Promise<GetClaimHistoryResponse> => executeBillingZambda(oystehr, 'get-billing-claim-history', parameters);

export const exportClaimX12 = (
  oystehr: Oystehr,
  parameters: z.input<typeof ExportClaimX12InputSchema>
): Promise<ExportClaimX12Response> => executeBillingZambda(oystehr, 'export-billing-claim-x12', parameters);

export const getBillingClaimCms1500 = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetClaimCms1500InputSchema>
): Promise<Cms1500FormData> => executeBillingZambda(oystehr, 'get-billing-claim-cms1500', parameters);

export const updateBillingResource = (
  oystehr: Oystehr,
  parameters: z.input<typeof UpdateBillingResourceInputSchema>
): Promise<SavedResourceResponse> => executeBillingZambda(oystehr, 'update-billing-claim', parameters);

export const tagBillingClaim = (
  oystehr: Oystehr,
  parameters: z.input<typeof TagBillingClaimInputSchema>
): Promise<OkResponse> => executeBillingZambda(oystehr, 'tag-billing-claim', parameters);

export const addBillingClaimNote = (
  oystehr: Oystehr,
  parameters: z.input<typeof AddClaimNoteInputSchema>
): Promise<OkResponse> => executeBillingZambda(oystehr, 'add-billing-claim-note', parameters);

// --- Providers ---

export const createBillingProvider = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateBillingProviderInputSchema>
): Promise<CreatedResourceResponse> => executeBillingZambda(oystehr, 'create-billing-provider', parameters);

export const searchBillingProviders = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingProvidersInputSchema>
): Promise<SearchBillingProvidersResponse> => executeBillingZambda(oystehr, 'search-billing-providers', parameters);

export const getBillingProvider = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetBillingProviderInputSchema>
): Promise<BillingProviderOption> => executeBillingZambda(oystehr, 'get-billing-provider', parameters);

export const updateBillingProvider = (
  oystehr: Oystehr,
  parameters: z.input<typeof UpdateBillingProviderInputSchema>
): Promise<CreatedResourceResponse> => executeBillingZambda(oystehr, 'update-billing-provider', parameters);

export const deleteBillingProvider = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteBillingProviderInputSchema>
): Promise<DeletedResponse> => executeBillingZambda(oystehr, 'delete-billing-provider', parameters);

// --- Lookups (payers, locations, coverages) ---

export const searchBillingServices = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingServicesInputSchema>
): Promise<SearchBillingServicesResponse> => executeBillingZambda(oystehr, 'search-billing-services', parameters);

export const searchBillingPayers = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingPayersInputSchema>
): Promise<SearchBillingPayersResponse> => executeBillingZambda(oystehr, 'search-billing-payers', parameters);

export const searchBillingLocations = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchBillingLocationsInputSchema>
): Promise<SearchBillingLocationsResponse> => executeBillingZambda(oystehr, 'search-billing-locations', parameters);

export const getPatientCoverages = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetPatientCoveragesInputSchema>
): Promise<GetPatientCoveragesResponse> => executeBillingZambda(oystehr, 'get-patient-coverages', parameters);

export const createBillingCoverage = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateBillingCoverageInputSchema>
): Promise<CreatedResourceResponse> => executeBillingZambda(oystehr, 'create-billing-coverage', parameters);

export const updateBillingCoverage = (
  oystehr: Oystehr,
  parameters: z.input<typeof UpdateBillingCoverageInputSchema>
): Promise<SavedResourceResponse> => executeBillingZambda(oystehr, 'update-billing-coverage', parameters);

export const getBillingCoverage = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetBillingCoverageInputSchema>
): Promise<GetBillingCoverageResponse> => executeBillingZambda(oystehr, 'get-billing-coverage', parameters);

export const deleteBillingCoverage = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteBillingCoverageInputSchema>
): Promise<DeletedResponse> => executeBillingZambda(oystehr, 'delete-billing-coverage', parameters);

// --- Service Facilities ---

export const searchBillingServiceFacilities = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchServiceFacilitiesInputSchema>
): Promise<SearchServiceFacilitiesResponse> =>
  executeBillingZambda(oystehr, 'search-billing-service-facilities', parameters);

export const getBillingServiceFacility = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetServiceFacilityInputSchema>
): Promise<ServiceFacilityItem> => executeBillingZambda(oystehr, 'get-billing-service-facility', parameters);

export const saveBillingServiceFacility = (
  oystehr: Oystehr,
  parameters: z.input<typeof SaveServiceFacilityInputSchema>
): Promise<SavedResourceResponse> => executeBillingZambda(oystehr, 'save-billing-service-facility', parameters);

export const deleteBillingServiceFacility = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteServiceFacilityInputSchema>
): Promise<DeletedResponse> => executeBillingZambda(oystehr, 'delete-billing-service-facility', parameters);

// --- Non-Insurance Organizations ---

export const createBillingNonInsuranceOrg = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateNonInsuranceOrgInputSchema>
): Promise<CreatedResourceResponse> => executeBillingZambda(oystehr, 'create-billing-non-insurance-org', parameters);

export const updateBillingNonInsuranceOrg = (
  oystehr: Oystehr,
  parameters: z.input<typeof UpdateNonInsuranceOrgInputSchema>
): Promise<SavedResourceResponse> => executeBillingZambda(oystehr, 'update-billing-non-insurance-org', parameters);

export const searchBillingNonInsuranceOrgs = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchNonInsuranceOrgsInputSchema>
): Promise<SearchNonInsuranceOrgsResponse> =>
  executeBillingZambda(oystehr, 'search-billing-non-insurance-orgs', parameters);

export const deleteBillingNonInsuranceOrg = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteNonInsuranceOrgInputSchema>
): Promise<DeletedResponse> => executeBillingZambda(oystehr, 'delete-billing-non-insurance-org', parameters);

// --- Insurance Organizations (custom) ---

export const createBillingCustomInsuranceOrg = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateCustomInsuranceOrgInputSchema>
): Promise<CreatedResourceResponse> => executeBillingZambda(oystehr, 'create-billing-custom-insurance-org', parameters);

export const updateBillingCustomInsuranceOrg = (
  oystehr: Oystehr,
  parameters: z.input<typeof UpdateCustomInsuranceOrgInputSchema>
): Promise<SavedResourceResponse> => executeBillingZambda(oystehr, 'update-billing-custom-insurance-org', parameters);

export const searchBillingCustomInsuranceOrgs = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchCustomInsuranceOrgsInputSchema>
): Promise<SearchCustomInsuranceOrgsResponse> =>
  executeBillingZambda(oystehr, 'search-billing-custom-insurance-orgs', parameters);

export const deleteBillingCustomInsuranceOrg = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteCustomInsuranceOrgInputSchema>
): Promise<DeletedResponse> => executeBillingZambda(oystehr, 'delete-billing-custom-insurance-org', parameters);

// --- Terminology ---

// CPT and HCPCS share the service-line code field, so search both and merge. Called straight from the
export const searchBillingProcedureCodes = async (
  oystehr: Oystehr,
  parameters: { query: string }
): Promise<SearchCodeResponse> => {
  const [cpt, hcpcs] = await Promise.all([
    oystehr.terminology.searchCpt({ query: parameters.query, searchType: 'all', limit: 50 }),
    oystehr.terminology.searchHcpcs({ query: parameters.query, searchType: 'all', limit: 50 }),
  ]);
  const seen = new Set<string>();
  const codes: BillingCodeOption[] = [];
  for (const c of [...cpt.codes, ...hcpcs.codes]) {
    if (seen.has(c.code)) continue;
    seen.add(c.code);
    codes.push({ code: c.code, display: c.display });
  }
  codes.sort((a, b) => a.code.localeCompare(b.code));
  return { codes };
};

export const searchBillingDiagnosisCodes = async (
  oystehr: Oystehr,
  parameters: { query: string }
): Promise<SearchCodeResponse> => {
  const icd = await oystehr.terminology.searchIcd10({ query: parameters.query, searchType: 'all', limit: 50 });
  const seen = new Set<string>();
  const codes: BillingCodeOption[] = [];
  for (const c of icd.codes) {
    if (seen.has(c.code)) continue;
    seen.add(c.code);
    codes.push({ code: c.code, display: c.display });
  }
  codes.sort((a, b) => a.code.localeCompare(b.code));
  return { codes };
};

// Display text for exact procedure codes (the ERA drill-in shows "I&D of Abscess (10060)").
// Submitted claims store bare codes, so the terminology service is the only description source;
// codes it doesn't know are omitted and the caller falls back to the bare code.
export const lookupProcedureDescriptions = async (
  oystehr: Oystehr,
  codes: string[]
): Promise<Record<string, string>> => {
  const descriptions: Record<string, string> = {};
  await Promise.all(
    [...new Set(codes.filter(Boolean))].map(async (code) => {
      try {
        const { codes: found } = await searchBillingProcedureCodes(oystehr, { query: code });
        const display = found.find((option) => option.code === code)?.display;
        if (display) descriptions[code] = display;
      } catch {
        // unknown code — bare code is shown instead
      }
    })
  );
  return descriptions;
};

// --- Tags ---

export const searchBillingTags = (oystehr: Oystehr): Promise<SearchBillingTagsResponse> =>
  executeBillingZambda(oystehr, 'search-billing-tags');

// --- Reports ---

// Unified cached-report endpoint; typed per-kind wrappers below.
const getBillingReport = <T>(
  oystehr: Oystehr,
  kind: RefreshReportKind,
  params?: Record<string, unknown>,
  refresh?: boolean,
  drilldown?: Record<string, unknown>
): Promise<T> =>
  executeBillingZambda(oystehr, 'get-billing-report', {
    kind,
    ...(params && Object.keys(params).length > 0 ? { params } : {}),
    ...(refresh ? { refresh: true } : {}),
    ...(drilldown ? { drilldown } : {}),
  });

export const getBillingPaymentsReport = (
  oystehr: Oystehr,
  params?: ReportDateWindowParams,
  refresh?: boolean
): Promise<GetBillingPaymentsReportResponse> =>
  getBillingReport(oystehr, 'payments', params as Record<string, unknown>, refresh);

export const getBillingPatientPaymentsReport = (
  oystehr: Oystehr,
  params?: ReportDateWindowParams,
  refresh?: boolean
): Promise<GetBillingPatientPaymentsReportResponse> =>
  getBillingReport(oystehr, 'patient-payments', params as Record<string, unknown>, refresh);

export const getBillingInvoiceReport = (
  oystehr: Oystehr,
  _params?: undefined,
  refresh?: boolean
): Promise<GetBillingInvoiceReportResponse> => getBillingReport(oystehr, 'invoice', undefined, refresh);

export const getBillingCardsOnFileReport = (
  oystehr: Oystehr,
  _params?: undefined,
  refresh?: boolean
): Promise<GetBillingCardsOnFileReportResponse> => getBillingReport(oystehr, 'cards-on-file', undefined, refresh);

export const getBillingPipelineReport = (
  oystehr: Oystehr,
  params?: ReportDateWindowParams,
  refresh?: boolean
): Promise<GetBillingPipelineReportResponse> =>
  getBillingReport(oystehr, 'pipeline', params as Record<string, unknown>, refresh);

export const getBillingProductivityReport = (
  oystehr: Oystehr,
  params?: ReportDateWindowParams,
  refresh?: boolean
): Promise<GetBillingProductivityReportResponse> =>
  getBillingReport(oystehr, 'productivity', params as Record<string, unknown>, refresh);

export const getBillingNetCollectionsReport = (
  oystehr: Oystehr,
  params?: ReportDateWindowParams,
  refresh?: boolean
): Promise<GetBillingNetCollectionsReportResponse> =>
  getBillingReport(oystehr, 'net-collections', params as Record<string, unknown>, refresh);

// this kind's cached runs, newest first
export const getBillingReportHistory = (
  oystehr: Oystehr,
  kind: RefreshReportKind
): Promise<GetBillingReportHistoryResponse> =>
  executeBillingZambda(oystehr, 'get-billing-report', { kind, history: true });

// ERA drilldown over the payments report's cached detail
export const getBillingPaymentsReportDrilldown = (
  oystehr: Oystehr,
  drilldown: z.input<typeof GetBillingPaymentsReportDrilldownInputSchema>
): Promise<GetBillingPaymentsReportDrilldownResponse> =>
  getBillingReport(oystehr, 'payments', undefined, undefined, drilldown as Record<string, unknown>);

// row-filtered drilldown over the window's cached detail
export const getBillingPatientPaymentsDrilldown = (
  oystehr: Oystehr,
  params: ReportDateWindowParams,
  drilldown: z.input<typeof PatientPaymentsDrilldownParamsSchema>
): Promise<GetBillingPatientPaymentsDrilldownResponse> =>
  getBillingReport(
    oystehr,
    'patient-payments',
    params as Record<string, unknown>,
    undefined,
    drilldown as Record<string, unknown>
  );

// per-payer ERA drilldown over the window's cached net-collections detail
export const getBillingNetCollectionsDrilldown = (
  oystehr: Oystehr,
  params: ReportDateWindowParams,
  drilldown: z.input<typeof NetCollectionsDrilldownParamsSchema>
): Promise<GetBillingNetCollectionsDrilldownResponse> =>
  getBillingReport(
    oystehr,
    'net-collections',
    params as Record<string, unknown>,
    undefined,
    drilldown as Record<string, unknown>
  );

export const saveBillingTag = (
  oystehr: Oystehr,
  parameters: z.input<typeof SaveBillingTagInputSchema>
): Promise<SavedResourceResponse> => executeBillingZambda(oystehr, 'save-billing-tag', parameters);

export const deleteBillingTag = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteBillingTagInputSchema>
): Promise<DeletedResponse> => executeBillingZambda(oystehr, 'delete-billing-tag', parameters);

// --- ERAs ---

export const searchBillingEras = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchErasInputSchema>
): Promise<SearchBillingErasResponse> => executeBillingZambda(oystehr, 'search-billing-eras', parameters);

export const getBillingEraDetail = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetEraDetailInputSchema>
): Promise<EraDetailResponse> => executeBillingZambda(oystehr, 'get-billing-era-detail', parameters);

export const importEra = (oystehr: Oystehr, parameters: z.input<typeof ImportEraInputSchema>): Promise<any> =>
  executeBillingZambda(oystehr, 'import-era', parameters);

export const saveBillingManualEra = (
  oystehr: Oystehr,
  parameters: z.input<typeof SaveManualEraInputSchema>
): Promise<SaveManualEraResponse> => executeBillingZambda(oystehr, 'save-billing-manual-era', parameters);

export const matchClaimResponseToClaim = (
  oystehr: Oystehr,
  parameters: z.input<typeof MatchClaimResponseToClaimInputSchema>
): Promise<any> => executeBillingZambda(oystehr, 'match-claim-response', parameters);

export const unmatchClaimResponse = (
  oystehr: Oystehr,
  parameters: z.input<typeof UnmatchClaimResponseInputSchema>
): Promise<any> => executeBillingZambda(oystehr, 'unmatch-claim-response', parameters);

// --- ChargeItemDefinitions --

export const searchChargeItemDefinitions = (
  oystehr: Oystehr,
  parameters: z.input<typeof SearchChargeItemDefinitionsInputSchema>
): Promise<SearchChargeItemDefinitionsResponse> =>
  executeBillingZambda(oystehr, 'search-charge-item-definitions', parameters);

export const createChargeItemDefinition = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateChargeItemDefinitionInputSchema>
): Promise<BillingChargeItemDefinition> => executeBillingZambda(oystehr, 'create-charge-item-definition', parameters);

export const getChargeItemDefinition = (
  oystehr: Oystehr,
  parameters: z.input<typeof GetChargeItemDefinitionInputSchema>
): Promise<BillingChargeItemDefinition> => executeBillingZambda(oystehr, 'get-charge-item-definition', parameters);

export const updateChargeItemDefinition = (
  oystehr: Oystehr,
  parameters: z.input<typeof UpdateChargeItemDefinitionInputSchema>
): Promise<BillingChargeItemDefinition> => executeBillingZambda(oystehr, 'update-charge-item-definition', parameters);

export const deleteChargeItemDefinition = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteChargeItemDefinitionInputSchema>
): Promise<void> => executeBillingZambda(oystehr, 'delete-charge-item-definition', parameters);

export const bulkAddChargeItemDefinitionProcedureCodes = (
  oystehr: Oystehr,
  parameters: z.input<typeof BulkAddChargeItemDefinitionProcedureCodesInputSchema>
): Promise<BillingChargeItemDefinition> =>
  executeBillingZambda(oystehr, 'bulk-add-charge-item-definition-procedure-codes', parameters);

// --- Payments ---

export const recordBillingManualPayment = (
  oystehr: Oystehr,
  parameters: z.input<typeof RecordBillingManualPaymentInputSchema>
): Promise<RecordBillingManualPaymentResponse> =>
  executeBillingZambda(oystehr, 'record-billing-manual-payment', parameters);

// --- Claim Write-offs ---

export const addClaimWriteOff = (
  oystehr: Oystehr,
  parameters: z.input<typeof AddClaimWriteOffInputSchema>
): Promise<void> => executeBillingZambda(oystehr, 'add-claim-write-off', parameters);

export const voidClaimWriteOff = (
  oystehr: Oystehr,
  parameters: z.input<typeof VoidClaimWriteOffInputSchema>
): Promise<void> => executeBillingZambda(oystehr, 'void-claim-write-off', parameters);

// --- Claim Attachments ---

export const addClaimAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof AddClaimAttachmentInputSchema>
): Promise<AddClaimAttachmentResponse> => executeBillingZambda(oystehr, 'add-claim-attachment', parameters);

export const renameClaimAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof RenameClaimAttachmentInputSchema>
): Promise<void> => executeBillingZambda(oystehr, 'rename-claim-attachment', parameters);

export const deleteClaimAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteClaimAttachmentInputSchema>
): Promise<void> => executeBillingZambda(oystehr, 'delete-claim-attachment', parameters);

export const downloadClaimAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof DownloadClaimAttachmentInputSchema>
): Promise<DownloadClaimAttachmentResponse> => executeBillingZambda(oystehr, 'download-claim-attachment', parameters);

export const createTimelyFilingReport = (
  oystehr: Oystehr,
  parameters: z.input<typeof CreateTimelyFilingReportInputSchema>
): Promise<CreateTimelyFilingReportResponse> =>
  executeBillingZambda(oystehr, CREATE_TIMELY_FILING_REPORT_ZAMBDA, parameters);

// --- ERA Attachments ---

export const addEraAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof AddEraAttachmentInputSchema>
): Promise<AddEraAttachmentResponse> => executeBillingZambda(oystehr, 'add-era-attachment', parameters);

export const renameEraAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof RenameEraAttachmentInputSchema>
): Promise<OkResponse> => executeBillingZambda(oystehr, 'rename-era-attachment', parameters);

export const deleteEraAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof DeleteEraAttachmentInputSchema>
): Promise<DeletedResponse> => executeBillingZambda(oystehr, 'delete-era-attachment', parameters);

export const downloadEraAttachment = (
  oystehr: Oystehr,
  parameters: z.input<typeof DownloadEraAttachmentInputSchema>
): Promise<DownloadEraAttachmentResponse> => executeBillingZambda(oystehr, 'download-era-attachment', parameters);
